// src/components/DisplayTab.js
import React, { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import { Button } from "@material-tailwind/react";
import CardFactory from "../../factories/CardFactory";
import InputFactory from "../../factories/InputFactory";
import { cardRegistry } from "../../cards/cardRegistry";
import { updateCardDisplayType } from "../../../api/api";

const ConfigurationTab = ({ token, card, onSave, modalRef }) => {
  const { scrollY } = useScroll({ container: modalRef });
  const scaledHeight = useTransform(scrollY, [0, 300], ["246px", "174px"]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.6]);

  if (!card.subtype) {
    card.subtype = "auto";
  }

  if (!card.variant) {
    card.variant = "default";
  }

  // Initialize state based on card's current subtype and parameters
  const [selectedCardType, setSelectedCardType] = useState(card.subtype);
  const [selectedVariant, setSelectedVariant] = useState(card.variant);
  const [parameters, setParameters] = useState(card.params || {});

  // Handle changes to parameter fields
  const handleParameterChange = (key, value) => {
    setParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle saving the updated display settings
  const handleSave = () => {
    updateCardDisplayType(
      token,
      card.id,
      selectedCardType,
      selectedVariant,
      parameters
    );
    onSave();
  };

  /**
   * Renders parameter input fields based on the selected card type and variant.
   */
  const renderParameterFields = () => {
    if (!cardRegistry[card.type][selectedCardType].parameters[selectedVariant])
      return null;

    return Object.entries(
      cardRegistry[card.type][selectedCardType].parameters[selectedVariant]
    ).map(([key, config]) => {
      const commonProps = {
        label: config.label,
        value: parameters[key] !== undefined ? parameters[key] : config.default,
        onChange: (value) => handleParameterChange(key, value),
        options: config.options, // Only applicable for 'option' type
        min: config.min, // Only applicable for 'int' type
        max: config.max, // Only applicable for 'int' type
      };

      return (
        <React.Fragment key={key}>
          {InputFactory({ type: config.type, ...commonProps })}
        </React.Fragment>
      );
    });
  };

  /**
   * Renders previews for each card type.
   */
  const renderCardTypePreviews = () => {
    return Object.keys(cardRegistry[card.type]).map((subtype) => {
      // Optionally exclude certain card types
      if (["loghook"].includes(subtype)) return null;

      const previewCard = {
        ...card,
        subtype: subtype,
        variant: cardRegistry[card.type][subtype].variants[0],
      };
      const isSelected = subtype === selectedCardType;

      return (
        <div
          key={subtype}
          className={`p-2 rounded cursor-pointer ${
            isSelected
              ? "border border-blue-500 bg-blue-50"
              : "border-gray-300 bg-white"
          }`}
          onClick={() => {
            setSelectedCardType(subtype);
            setSelectedVariant(cardRegistry[card.type][subtype].variants[0]);
            setParameters({});
          }}
        >
          <h4 className="text-sm font-medium text-center text-gray-600 capitalize">
            {subtype}
          </h4>
          <div className="w-52 h-52 pointer-events-none">
            <CardFactory
              card={previewCard}
              topic={{
                id: card.topics[0].id,
              }}
              w={2}
              h={2}
              breakpoint="lg"
            />
          </div>
        </div>
      );
    });
  };

  const renderDisplayVariantPreviews = () => {
    const variants = cardRegistry[card.type][selectedCardType]?.variants || [];
    return variants.map((variant) => {
      const previewCard = {
        ...card,
        subtype: selectedCardType,
        variant: variant,
      };

      const isSelected = variant === selectedVariant;

      return (
        <div
          key={variant}
          className={`p-2 rounded cursor-pointer ${
            isSelected
              ? "border border-green-500 bg-green-50"
              : "border-gray-300 bg-white"
          }`}
          onClick={() => {
            setSelectedVariant(variant);
            setParameters({});
          }}
        >
          <h4 className="text-sm font-medium text-center text-gray-600 capitalize">
            {variant}
          </h4>
          <div className="w-52 h-52 pointer-events-none">
            <CardFactory
              card={previewCard}
              topic={{
                id: card.topics[0].id,
              }}
              w={2}
              h={2}
              breakpoint="lg"
              displayParameters={parameters}
            />
          </div>
        </div>
      );
    });
  };

  const hasParameters =
    cardRegistry[card.type][selectedCardType]?.parameters[selectedVariant] &&
    Object.keys(
      cardRegistry[card.type][selectedCardType].parameters[selectedVariant]
    ).length > 0;

  return (
    <div className="flex flex-col h-full gap-10">
      {/* Display Type Section */}
      <div>
        <h4 className="text-md font-semibold mb-1">Type</h4>
        <div className="flex flex-row overflow-auto gap-4">
          {renderCardTypePreviews()}
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-white">
        {/* Display Variant Section - Sticky */}
        {hasParameters && (
          <div className="sticky top-0 z-30 overflow-auto shadow">
            <h4 className="text-md font-semibold mb-1">Variant</h4>
            <motion.div
              style={{
                height: scaledHeight,
                transformOrigin: "top left",
                scale,
              }}
            >
              <div className="flex flex-row gap-4">
                {renderDisplayVariantPreviews()}
              </div>
            </motion.div>
          </div>
        )}
        {!hasParameters && (
          <div>
            <h4 className="text-md font-semibold mb-1">Variant</h4>
            <div className="mb-6 flex flex-row overflow-auto gap-4">
              {renderDisplayVariantPreviews()}
            </div>
          </div>
        )}
      </div>

      {/* Parameter Section */}
      {hasParameters && (
        <div className="flex-grow overflow-y-auto min-h-[20rem]">
          <div className="mb-6">
            <h4 className="text-md font-semibold mb-2">Parameters</h4>
            <div className="xl:grid xl:grid-cols-2 xl:gap-6">
              {renderParameterFields()}
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          color="indigo"
          onClick={handleSave}
          ripple={true}
          className="text-sm"
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default ConfigurationTab;
